import { useCallback, useMemo } from 'react';
import { TicketDetailPanel } from '../../components/detail/TicketDetailPanel';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, Notice, StaleBanner } from '../../components/ui/Notice';
import { SkeletonBar } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { useProjects } from '../../projects/ProjectsProvider';
import { fetchProjectTickets, TICKET_WINDOW_MAX } from '../board/project-tickets';
import { ReleaseHistoryView } from './ReleaseHistoryView';
import { ReleaseVersionDialog } from './ReleaseVersionDialog';
import { VersionCard } from './VersionCard';
import { VersionFormDialog } from './VersionFormDialog';
import {
  buildReleaseHistory,
  buildVersionRollups,
  derivedVersionNames,
  hasFixVersionData,
  orphanFixVersionNames,
  sortVersionsForDisplay,
} from './version-rollup';
import { fetchVersions, versionsPath, type VersionsResult } from './versions-api';
import { useVersionActions } from './useVersionActions';
import { useVersionsParams } from './useVersionsParams';

/**
 * VERSIONS AND RELEASE HISTORY.
 *
 * 🔴 TWO RELATIONSHIPS, NEVER MERGED: `ticket.version` (planned for) and `fix_version` (shipped in,
 * MULTI-VALUED). See `version-rollup.ts` — the counts are separate and there is no combined total.
 *
 * ⛔⛔ AND THE VERSIONS ENDPOINT MAY NOT EXIST. When it does not, this page SAYS SO, names the path,
 * and shows only what tickets can honestly prove — clearly labelled as derived, with the release
 * history absent rather than empty. It does not fill the gap with a fixture and look finished; a
 * fixture kinder than the API is exactly how the last defect here reached production.
 *
 * ⚠️ Renders no app chrome — the shell owns `App.tsx` and `components/layout/`. Must be inside
 * `VocabularyProvider` (status chips and type icons read from it).
 */
export function VersionsPage() {
  const { projectId, selectedId, tab, setTab, select } = useVersionsParams();

  /**
   * ⭐ The SHELL's project list and the SHELL's `?project=`. One control for one piece of state — the
   * rail and the top-bar switcher already choose the project, and a second picker in this header
   * would be a second thing to keep in step.
   */
  const projects = useProjects();

  const versions = useResource<VersionsResult>(
    (signal) => fetchVersions(projectId ?? '', signal),
    [projectId],
    { enabled: Boolean(projectId) },
  );

  /**
   * ⚠️ The tickets are a bounded WINDOW, and there is no server-side filter by version — `GET
   * /tickets` accepts projectId, status, assignee, type, priority, sprintId and parentId, and nothing
   * about versions. So every per-version list here is assembled client-side from what was loaded, and
   * its counts are FLOORS when the window is short of the project. That is stated on screen.
   */
  const tickets = useResource(
    (signal) => fetchProjectTickets(projectId ?? '', signal),
    [projectId],
    { enabled: Boolean(projectId) },
  );

  const items = useMemo(() => tickets.data?.items ?? [], [tickets.data]);
  /**
   * ⭐ Only relevant where the API sent no total of its own. `GET /projects/:id/versions` counts both
   * relationships over the WHOLE project, so those numbers are exact and this flag never weakens
   * them — it decides how a count is written only when there is nothing authoritative to write.
   */
  const windowIsComplete = tickets.data ? tickets.data.complete : true;

  const endpointMissing = versions.data?.kind === 'endpoint-missing';
  /**
   * ⭐⭐ `haveTheList` is not `versionList.length > 0`. An EMPTY list we successfully fetched is a
   * fact about the project; an empty list because the request FAILED is not a fact about anything —
   * and several things below are claims that are only true if we actually have the list.
   */
  const loaded = versions.data;
  const haveTheList = loaded?.kind === 'ok';
  const versionList = loaded?.kind === 'ok' ? loaded.versions : [];

  const rollups = useMemo(
    () => sortVersionsForDisplay(buildVersionRollups(versionList, items)),
    [versionList, items],
  );
  const history = useMemo(() => buildReleaseHistory(rollups), [rollups]);
  const orphans = useMemo(
    // ⛔ Only when we HAVE the list. Without it, "these versions are not in the project" is a
    // disagreement between a real list and an absent one — i.e. not a disagreement at all, and
    // saying so pins a data problem on tickets that are fine.
    () => (haveTheList ? orphanFixVersionNames(versionList, items) : []),
    [haveTheList, versionList, items],
  );
  const derived = useMemo(() => derivedVersionNames(items), [items]);
  const fixVersionsPresent = items.length === 0 || hasFixVersionData(items);

  const onOpenTicket = useCallback((ticketId: string) => select(ticketId), [select]);

  /**
   * 🔴 PH-28's WRITE HALF. ⛔ Both resources are reloaded after every successful write, never patched
   * in place: `release` stamps a date the SERVER chose the format of, `unarchive` derives a state
   * from `release_date`, and the two ticket counts are the API's own over the whole project. Guessing
   * any of those locally is how "stale looks current" gets onto a release page.
   */
  const reloadAll = useCallback(() => {
    versions.reload();
    tickets.reload();
  }, [versions, tickets]);

  const actions = useVersionActions(projectId, reloadAll);

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)]">
      <header className="flex flex-wrap items-center gap-3 border-b border-[color:var(--color-line)] px-3 py-2 sm:px-4">
        <h1 className="text-base font-semibold">Versions &amp; releases</h1>
        <span className="flex-1" />
        {/**
         * 🔴 THE BUTTON PH-28 IS ABOUT. Before today this page called no POST at all, so a release
         * could only be created with curl.
         * ⛔ Rendered only once we HAVE the list. Offering "New version" beside an endpoint-missing
         * notice would invite a POST to a path this build has just been told is not deployed.
         */}
        {haveTheList ? (
          <button
            type="button"
            onClick={actions.openCreate}
            disabled={actions.busyId !== null}
            className="h-11 rounded bg-[color:var(--color-accent)] px-3 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50 sm:h-8"
          >
            New version
          </button>
        ) : null}
        <nav className="flex gap-1" aria-label="Versions views">
          {(['versions', 'history'] as const).map((entry) => (
            <button
              key={entry}
              type="button"
              onClick={() => setTab(entry)}
              aria-current={tab === entry}
              className={`h-8 rounded-full px-3 text-xs font-semibold ${
                tab === entry
                  ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                  : 'bg-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
              }`}
            >
              {entry === 'versions' ? 'Versions' : 'Release history'}
            </button>
          ))}
        </nav>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3 p-3 sm:p-4">
          {projects.error ? <ErrorNotice error={projects.error} onRetry={projects.reload} /> : null}

          {versions.error && !versions.isStale ? (
            <ErrorNotice error={versions.error} onRetry={versions.reload} />
          ) : null}
          {versions.isStale && versions.error ? (
            <StaleBanner error={versions.error} loadedAt={versions.loadedAt} onRetry={versions.reload} />
          ) : null}

          {tickets.error && !tickets.isStale ? (
            <ErrorNotice error={tickets.error} onRetry={tickets.reload} />
          ) : null}
          {tickets.isStale && tickets.error ? (
            <StaleBanner error={tickets.error} loadedAt={tickets.loadedAt} onRetry={tickets.reload} />
          ) : null}

          {endpointMissing ? <EndpointMissingNotice path={versionsPath(projectId ?? ':id')} /> : null}

          {/* ⭐ A write with no trace on screen reads as "did that work?" — and a new version appears
              far down a list sorted unreleased-first, where it is easy to miss. */}
          {actions.outcome ? (
            <Notice
              tone="info"
              title={actions.outcome}
              action={
                <button
                  type="button"
                  onClick={actions.dismissOutcome}
                  className="rounded border border-current/30 px-2.5 py-1 text-xs font-semibold"
                >
                  Dismiss
                </button>
              }
            />
          ) : null}

          {!windowIsComplete && tickets.data ? (
            <Notice
              tone="info"
              title={`Listing tickets from the first ${items.length} of ${tickets.data.total} in this project`}
            >
              <p>
                There is no version filter on <code>GET /tickets</code>, so the LISTS below are
                assembled from a window that stops at {TICKET_WINDOW_MAX}. ⭐ The COUNTS are the API's
                own, over the whole project, so where a list is shorter than its count each section
                says how many are missing. ⛔ Neither number is adjusted to make them agree.
              </p>
            </Notice>
          ) : null}

          {!fixVersionsPresent ? (
            <Notice tone="warning" title="The tickets came back without a fix_versions field">
              <p>
                Every "shipped in" list below is therefore EMPTY BECAUSE WE CANNOT SEE IT, not because
                nothing shipped. ⛔ The two are not the same fact and this one is a contract gap.
              </p>
            </Notice>
          ) : null}

          {orphans.length > 0 ? (
            <Notice tone="info" title="Some tickets name a fix version this project does not list">
              <p>
                {orphans.join(', ')} — the versions list and the tickets disagree. Shown here rather
                than dropped, because a name on a ticket is evidence the version exists somewhere.
              </p>
            </Notice>
          ) : null}
        </div>

        <div className="px-3 pb-6 sm:px-4">
          {!projectId ? (
            <EmptyState
              title="Choose a project"
              detail="Versions belong to a project — `version` has a `project_id` and names are unique only within one. Pick a project in the rail."
            />
          ) : null}

          {projectId && (versions.isLoading || tickets.isLoading) ? <VersionsSkeleton /> : null}

          {projectId && endpointMissing ? (
            <DerivedVersionNames names={derived} loaded={items.length} />
          ) : null}

          {projectId && haveTheList && !versions.isLoading ? (
            rollups.length === 0 ? (
              <EmptyState
                title="This project has no versions yet"
                detail="The request succeeded and returned none — an empty list, not a failure. Press New version to create one; it is born unreleased."
              />
            ) : tab === 'versions' ? (
              <div className="space-y-3">
                {rollups.map((rollup) => (
                  <VersionCard
                    key={rollup.version.id}
                    rollup={rollup}
                    windowIsComplete={windowIsComplete}
                    busy={actions.busyId === rollup.version.id}
                    error={actions.rowErrors[String(rollup.version.id)] ?? null}
                    onAction={actions.start}
                    onOpenTicket={(ticket) => onOpenTicket(ticket.id)}
                  />
                ))}
              </div>
            ) : (
              <ReleaseHistoryView
                history={history}
                windowIsComplete={windowIsComplete}
                onOpenTicket={(ticket) => onOpenTicket(ticket.id)}
              />
            )
          ) : null}
        </div>
      </div>

      {actions.creating ? (
        <VersionFormDialog
          busy={actions.busyId !== null}
          error={actions.dialogError}
          onCancel={actions.cancelCreate}
          onSubmit={actions.submitCreate}
        />
      ) : null}

      {actions.releasing ? (
        <ReleaseVersionDialog
          version={actions.releasing}
          busy={actions.busyId !== null}
          error={actions.dialogError}
          onCancel={actions.cancelRelease}
          onConfirm={actions.confirmRelease}
        />
      ) : null}

      {selectedId ? (
        <div className="fixed inset-0 z-40 lg:absolute lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[30rem]">
          <TicketDetailPanel
            ticketId={selectedId}
            onClose={() => select(null)}
            onOpenTicket={(id) => select(id)}
            onStatusChanged={() => {
              tickets.reload();
              versions.reload();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * 🔴 THE API GAP, ON SCREEN. Named, with the exact path, so nobody has to guess which half is
 * missing — and so this page cannot be mistaken for a finished one.
 */
function EndpointMissingNotice({ path }: { path: string }) {
  return (
    <Notice tone="error" title="The versions endpoint does not exist yet">
      <p>
        <code>GET /api/v1{path}</code> is not deployed — the API answered{' '}
        <code>no such route</code>, which is its own 404 handler and not "this project has no
        versions".
      </p>
      <p className="mt-1">
        ⛔ Nothing below is a version list. There is no state, no release date, no description and no
        release history, and a version with no tickets cannot appear at all. What is shown is only the
        fix-version NAMES that tickets carry.
      </p>
    </Notice>
  );
}

function DerivedVersionNames({ names, loaded }: { names: readonly string[]; loaded: number }) {
  if (names.length === 0) {
    return (
      <EmptyState
        title="No version names appear on the tickets we could load"
        detail={`Read from ${loaded} tickets. This is not evidence that the project has no versions — the endpoint that would answer that is missing.`}
      />
    );
  }

  return (
    <section aria-label="Version names derived from tickets">
      <h2 className="text-xs font-bold tracking-wide text-[color:var(--color-ink-muted)] uppercase">
        Derived from tickets — NOT the version list
      </h2>
      <ul className="mt-2 flex flex-wrap gap-2">
        {names.map((name) => (
          <li
            key={name}
            className="rounded border border-dashed border-[color:var(--color-line)] px-2 py-1 text-sm"
          >
            {name}
            <span className="ml-1.5 text-[10px] text-[color:var(--color-ink-muted)]">
              state unavailable
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VersionsSkeleton() {
  return (
    <div role="status" aria-label="Loading versions" className="space-y-3">
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="space-y-2 rounded-md border border-[color:var(--color-line)] p-3">
          <SkeletonBar className="h-4 w-32" />
          <SkeletonBar className="h-3 w-64" />
          <SkeletonBar className="h-16 w-full" />
        </div>
      ))}
    </div>
  );
}

export default VersionsPage;
